<template>
  <div class="box-container-main" :style="{ minWidth: minWidth }">
    <img
      v-if="isCollapsible"
      class="title"
      src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
      height="20"
      width="20"
      :style="{ backgroundColor: background, transform: `rotate(${boxCollapsed ? 180 : 0}deg)` }"
      @click="collapseArrowClicked"
    />
    <span
      v-if="isRequired"
      class="required-asterisks"
      :style="{ backgroundColor: background, left: isCollapsible ? '25px' : '14px' }"
      >**</span
    >
    <div v-if="!isCheckBox" class="title" :style="titleStyle" @click="isCollapsible && collapseArrowClicked()">{{ title }}</div>
    <CheckBoxV2
      v-else
      class="title"
      :title="title"
      :isChecked.sync="titleChecked"
      :isDisabled="checkBoxIsDisabled"
      @checkBoxClicked="cbClicked"
      :style="titleStyle"
    />
    <div
      v-if="isCollapsible"
      class="box-slot-container"
      ref="boxSlotContainer"
      :style="{ maxHeight: slotMaxHeight, overflow: slotOverflow }"
    >
      <slot></slot>
    </div>
    <slot v-else></slot>
    <!-- some old pages don't expect the extra div wrapping around slot, which will cause problmes -->
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  data: function () {
    return {
      titleChecked: false,
      expandedHeight: -1,
      boxWidth: -1,
      slotMaxHeight: '',
      boxCollapsed: false,

      resizeObserverTimeoutID: null,
    };
  },
  props: {
    title: { type: String, default: '' }, // The title of the box container
    isRequired: { type: Boolean, default: false }, // controls whether red asterisks next to title are shown or hidden
    isCheckBox: { type: Boolean, default: false }, // Set to true if the title is a checkbox
    isChecked: { type: Boolean, default: false }, // Set to true if the title is a checkbox and is checked
    checkBoxIsDisabled: { type: Boolean, default: false }, // controls whether the checkbox is disabled or enabled
    background: { type: String, default: 'white' }, // Change the background of the title (only if you're using different background color)
    isCollapsible: { type: Boolean, default: false }, // Set to true if the box container is collapsible
    collapsedHeight: { type: Number, default: 0 }, // Change the max height of the box container when it is collapsed
    isCollapsed: { type: Boolean, default: false }, // Set to true to collapse the box, false to expand (use .sync)
    minWidth: { type: Number, default: 100 }, // Set the minimum width of the box container

    // Public Events
    // @checkBoxClicked - return 2 parameters; first is the state of the checkbox - true/false, second the title of the box container
    // @collapseArrowClicked - return 2 parameters; first is whether the box is collapsed - true/false, second the title of the box container

    // Public Methods
    // updateExpandedHeight() - manually update expanded height; most scenarios should already be handled,
    //                          only need to call this method if for some reason the component fails to expand fully
  },
  computed: {
    titleStyle: function () {
      let left;
      if (this.isCollapsible && this.isRequired) {
        left = '44px';
      } else if (this.isCollapsible) {
        left = '25px';
      } else if (this.isRequired) {
        left = '32px';
      } else {
        left = '10px';
      }

      return {
        left,
        paddingLeft: '5px',
        backgroundColor: this.background,
        cursor: this.isCollapsible ? 'pointer' : 'default',
        'user-select': this.isCollapsible ? 'none' : 'all',
      };
    },
    slotOverflow: function () {
      return this.isCollapsible && this.boxCollapsed ? 'hidden' : ''; //adding this.boxCollapsed affected transition
    },
  },
  methods: {
    cbClicked: function (checked) {
      this.$emit('update:isChecked', checked);
      this.$emit('checkBoxClicked', checked, this.title);
    },
    collapseArrowClicked: function (pointerEvent) {
      this.boxCollapsed = !this.boxCollapsed;
      if (this.boxCollapsed) {
        this.slotMaxHeight = `${this.collapsedHeight}px`;
      } else {
        this.slotMaxHeight = `${this.expandedHeight}px`;
      }

      this.$emit('update:isCollapsed', this.boxCollapsed);
      this.$emit('collapseArrowClicked', this.boxCollapsed, this.title);
    },
    updateExpandedHeight: function () {
      // used whenever the boxcontainer is resized vertically/horizontally or if the contents in the boxcontainer changes
      const boxSlotContainer = this.$refs['boxSlotContainer'];
      if (!boxSlotContainer) {
        // for some reason, when the component is unloaded, this method will be called
        return; // don't do anything if boxSlotContainer is undefined (should only happen on destroy)
      }

      // this.slotMaxHeight = ''; // temporarily remove the max-height for slot container, so that it grows to fit the content
      setTimeout(() => {
        // this.expandedHeight = boxSlotContainer.getBoundingClientRect().height; // calculate the new expanded height
        this.expandedHeight = boxSlotContainer.scrollHeight; // calculate the new expanded height, use scroll height so do not have to expand and shrink which cause flickering issue
        this.slotMaxHeight = this.boxCollapsed ? `${this.collapsedHeight}px` : `${this.expandedHeight}px`; // reset max height accordingly
      }, 5); // wait for 0.005 seconds so that the removal of max-height can take effect
    },
  },
  mounted() {
    if (this.isChecked) {
      this.titleChecked = this.isChecked;
    }

    this.boxCollapsed = this.isCollapsed;

    if (this.isCollapsible) {
      if (this.boxCollapsed) {
        // if the box started off as collapsed
        this.slotMaxHeight = ''; // temporarily remove max-height
        const clientRect = this.$refs['boxSlotContainer'].getBoundingClientRect(); // calculate bounding rectangle for boxSlotContainer
        this.expandedHeight = clientRect.height; // store height needed to fit all content
        this.boxWidth = clientRect.width; // store current width
        this.slotMaxHeight = `${this.collapsedHeight}px`; // set max-height to collapsedHeight
      } else {
        const clientRect = this.$refs['boxSlotContainer'].getBoundingClientRect(); // calculate bounding rectangle for boxSlotContainer
        this.expandedHeight = clientRect.height; // store height needed to fit all content
        this.boxWidth = clientRect.width; // store current width
        this.slotMaxHeight = `${this.expandedHeight}px`; // set max-height to expandedHeight
      }

      // Callback when the boxContainerMain element width changes
      let resizeObserver = new ResizeObserver((e) => {
        if (this.boxWidth === e[0].contentRect.width) {
          // if width did not change
          return; // don't do anything
        }

        this.boxWidth = e[0].contentRect.width; // store new width
        if (this.resizeObserverTimeoutID) {
          // cancel the previous ResizeObserver callback if it hasn't ended yet
          clearTimeout(this.resizeObserverTimeoutID); // if the previous callback has ended, this won't throw an error, so doesn't matter
        }

        this.resizeObserverTimeoutID = setTimeout(async () => {
          // create a new timeout
          this.updateExpandedHeight(); // to update expanded height
        }, 100); // wait for 100ms, so that it will wait until the user is done dragging/resizing the box container
      });
      // Observing the changes of size of the boxContainerMain element
      resizeObserver.observe(this.$refs['boxSlotContainer']);

      // Callback for mutation observer (any child changes / add or remove)
      let mutationObserver = new MutationObserver(async (e) => {
        this.updateExpandedHeight();
      });
      // Observing the element changes
      mutationObserver.observe(this.$refs['boxSlotContainer'], { childList: true, subtree: true });
    } else {
      this.slotMaxHeight = ''; // no max-height if box is not collapsable
    }
  },
  watch: {
    isChecked: function (val) {
      this.titleChecked = val;
    },
    isCollapsed: function (val) {
      this.boxCollapsed = val;

      if (this.boxCollapsed) {
        this.slotMaxHeight = `${this.collapsedHeight}px`;
      } else {
        this.slotMaxHeight = `${this.expandedHeight}px`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.box-container-main {
  position: relative;
  border: 1px solid gray;
  padding: 5px;
  border-radius: 5px;
  margin-top: 10px;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  text-align: left;

  > img.title {
    padding: 0;
    cursor: pointer;
    transition: 0.3s; // for rotate
  }

  > .required-asterisks {
    position: absolute;
    top: -8px;
    padding-left: 4px;
    color: red;
  }

  > .title {
    position: absolute;
    top: -9px;
    padding-right: 5px;
  }

  > .box-slot-container {
    transition: 0.3s;
  }
}
</style>
